import axios from 'axios';
// config
import { HOST_API_KEY } from '../config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API_KEY });
let refreshRequest = null;

export const VENUE_STAFF_AUTH_URL = `${process.env.REACT_APP_BACKEND_URL_V2}/venue/staff/auth`;

const decodeTokenPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (error) {
    return null;
  }
};

// V2 staff sessions refresh through the V2 staff auth API; V1 sessions (e.g. impersonation) keep using authv.
export const isStaffSession = (refreshToken) => decodeTokenPayload(refreshToken)?.userType === 'staff';

export const getRefreshTokensUrl = (refreshToken) =>
  isStaffSession(refreshToken)
    ? `${VENUE_STAFF_AUTH_URL}/refresh-tokens`
    : `${process.env.REACT_APP_BACKEND_URL}/authv/refresh-tokens`;

const clearAuthentication = () => {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
  delete axiosInstance.defaults.headers.common.Authorization;
};

const handleUnauthorized = (client) => async (error) => {
  const originalRequest = error.config;
  const refreshToken = sessionStorage.getItem('refreshToken');
  const isRefreshRequest = originalRequest?.url?.includes('/refresh-tokens');

  if (error.response?.status === 401 && refreshToken && !originalRequest?._retry && !isRefreshRequest) {
    originalRequest._retry = true;
    try {
      if (!refreshRequest) {
        refreshRequest = axios.post(getRefreshTokensUrl(refreshToken), {
          refreshToken,
          browserInfo: { userAgent: window.navigator.userAgent },
        }).finally(() => {
          refreshRequest = null;
        });
      }
      const response = await refreshRequest;
      const tokens = response.data?.tokens || response.data;
      const accessToken = tokens?.access?.token;
      const nextRefreshToken = tokens?.refresh?.token;
      if (!accessToken || !nextRefreshToken) throw new Error('Invalid refresh response');

      sessionStorage.setItem('accessToken', accessToken);
      sessionStorage.setItem('refreshToken', nextRefreshToken);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return client(originalRequest);
    } catch (refreshError) {
      clearAuthentication();
      window.location.assign('/auth/login');
      return Promise.reject(refreshError?.response?.data || refreshError);
    }
  }

  if (error.response?.status === 401) {
    clearAuthentication();
    window.location.assign('/auth/login');
  }

  return Promise.reject((error.response && error.response.data) || error || 'Something went wrong');
};

axiosInstance.interceptors.response.use(
  (response) => response,
  handleUnauthorized(axiosInstance)
);

// ----------------------------------------------------------------------
// V2 API INSTANCE
// ----------------------------------------------------------------------

export const axiosV2 = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL_V2,
});

axiosV2.interceptors.request.use(
  (config) => {
    const token =
      sessionStorage.getItem('accessToken') ||
      localStorage.getItem('accessToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosV2.interceptors.response.use(
  (response) => response,
  handleUnauthorized(axiosV2)
);

export default axiosInstance;
